import { hasPermission } from '@/hooks/useBackendMenu'
import { CategoriaEnum } from '@acme/auth'
import dayjs from 'dayjs'
import { PreLoading } from 'global/components/LoadingCustumer'
import { useAuth } from 'hooks'
import { queryClient } from 'providers/ReactQuery'
import React, { useEffect } from 'react'
import {
    Cliente,
    FuncionarioUsuario,
    ajustes,
    clientes,
    empresas,
    funcionario,
    hasPermissionToAnyAfastamento,
    local,
} from 'service/api'
import { toast } from 'sonner'
import OverlayApresentacao, {
    IPasso,
} from 'templates/Apresentacao/OverlayApresentacao'
import { useStore } from '../store'
import ErrorBoundary from './ErrorBoundary'

type Props = {
    children: React.ReactNode
}

export const Preload: React.FC<Props> = ({ children }) => {
    const { auth, decoded, funcionarioSelecionado } = useAuth()
    const selecionado: Partial<FuncionarioUsuario> =
        decoded?.categoria === CategoriaEnum.Funcionario
            ? funcionarioSelecionado
            : ({} as any)
    const temPermissaoAjustes = hasPermissionAjustes()
    const visualizado = sessionStorage.getItem('visualizado')

    const cliente = useStore(state => state.customer)

    clientes.useFetchById(decoded?.cliente, {
        enabled: !!decoded?.cliente,
        onSuccess: data => {
            useStore.setState({ customer: data as unknown as Cliente })
        },
    })

    const setCompetence = useStore(state => state.setCompetencia)
    const [empresaId, empresa] = useStore(state => [
        state.companyId,
        state.company,
    ])

    const {
        data: companies,
        isFetched: isEmpresasFetched,
        refetch: refetchEmpresas,
    } = empresas.useFetch({
        enabled: auth && !!decoded?.cliente,
        onSuccess: companies => {
            if (companies?.length && empresaId === -1) {
                const company =
                    companies.find(c => c.id === selecionado?.empresaId) ??
                    companies[0]
                useStore.setState({ company, companyId: company.id })
            } else if (companies?.length && empresaId !== -1) {
                const index = companies.findIndex(c => c.id === empresaId)

                if (index !== -1) {
                    const company = companies[index]
                    useStore.setState({ company, companyId: company.id })
                } else {
                    const company = companies[0]
                    useStore.setState({ company, companyId: company.id })
                }
            }
        },
        refetchOnWindowFocus: false,
        refetchInterval: false,
        refetchIntervalInBackground: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
    })

    useEffect(() => {
        if (isEmpresasFetched && empresaId === -1) refetchEmpresas()
        if (isEmpresasFetched && empresaId > 0 && companies?.length) {
            const competencia =
                companies.find(c => c.id === empresaId)?.competenciaAtual ||
                companies[0].competenciaAtual ||
                dayjs().format('YYYY-MM')
            setCompetence(competencia)
        }
    }, [empresaId])

    useEffect(() => {
        if (!empresa?.competenciaAtual) return
        setCompetence(empresa)
    }, [empresa?.competenciaAtual])

    queryClient.setDefaultOptions({
        queries: {
            enabled: auth && empresaId !== -1,
        },
    })

    const employeeId = useStore(state => state.employeeId)

    funcionario.useFetch({
        queryParams: { empresaId: empresaId, page: 1, perPage: 25 },
        enabled: auth && empresaId > 0,
        onSuccess: (response: any) => {
            const employees = response?.data ?? response
            // Não há funcionários para a empresa
            if (!employees?.length)
                return useStore.setState({
                    employeeId: -1,
                    employee: null,
                })

            // Não há funcionário selecionado, selecionar o primeiro ativo
            const byId = employees.find((it: any) => it.id === employeeId)
            const employee =
                byId ||
                employees.filter((it: any) => !it.dtRescisao).at(0) ||
                employees[0]
            useStore.getState().setEmployeeId(employee.id)
        },
        refetchOnWindowFocus: false,
        refetchInterval: false,
        refetchIntervalInBackground: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
    })

    const locationId = useStore(state => state.locationId)

    local.useFetch({
        queryParams: { empresaId: empresaId },
        onSuccess: locations => {
            if (locations?.length && locationId === -1) {
                useStore.setState({
                    locationId: locations[0].id,
                    location: locations[0],
                })
            } else if (locations?.length && locationId !== -1) {
                const index = locations.findIndex(l => l.id === locationId)

                if (index !== -1) {
                    const location = locations[index]

                    useStore.setState({
                        locationId: location.id,
                        location,
                    })
                }
            }
        },
    })

    useEffect(() => {
        if (!companies?.length || visualizado || !temPermissaoAjustes) return

        ajustes
            .get<number>('count-pendentes')
            .then(data => {
                if (data > 0) {
                    toast.warning(
                        data > 1
                            ? `Você tem ${data} ajustes pendentes de aprovação`
                            : 'Você tem um ajuste pendente de aprovação',
                    )
                    sessionStorage.setItem('visualizado', 'true')
                }
            })
            .catch(() => {
                void 0
            })
    }, [companies?.length, visualizado, temPermissaoAjustes])

    useEffect(() => {
        if (!cliente || !decoded || [1, 7].includes(decoded?.categoria)) return

        if (!decoded.isAtivo) {
            const append = [3, 6].includes(decoded?.categoria)
                ? 'Visite "Meu plano" para saber mais.'
                : ''
            toast.warning(
                `Seu plano está inativo, por isso limitamos algumas funcionalidades. ${append}`,
            )
        }
    }, [cliente?.status, decoded?.isAtivo])

    const MemoizedOverlay = React.useMemo(
        () =>
            !decoded?.categoria ||
            ![CategoriaEnum.Cliente, CategoriaEnum.Avaliacao].includes(
                decoded.categoria,
            ) ? null : (
                <OverlayApresentacao
                    name="inicial"
                    passos={passosApresentacao}
                />
            ),
        [decoded?.categoria],
    )

    return (
        <ErrorBoundary>
            <React.Suspense fallback={<PreLoading />}>
                {MemoizedOverlay}
                {children}
            </React.Suspense>
        </ErrorBoundary>
    )
}

const isMobilePresentation = window.innerWidth <= 1024 // lg

const passosApresentacao: IPasso[] = [
    {
        title: 'Cadastre sua empresa',
        description: `Cadastre sua empresa clicando em "Adicionar" e preenchendo os dados. 
        Depois, clique em "CADASTRAR".`,
        getHighlightElement: () =>
            isMobilePresentation
                ? document.getElementById('sub-botao_Empresas_mobile')!
                : document.getElementById('sub-botao_Empresas')!,
        parentElements: isMobilePresentation
            ? [
                  () => document.getElementById('botao_menuVertical')!,
                  () => document.getElementById('botao_Cadastros_mobile')!,
              ]
            : [() => document.getElementById('botao_Cadastros')!],
    },
    {
        title: 'Crie as regras de ponto',
        description: `Registre regras de cálculo para sua empresa, incluindo banco de horas, 
            compensação e porcentagens de pagamento. Digite um nome para a regra e 
            clique em "CADASTRAR".`,
        getHighlightElement: () =>
            isMobilePresentation
                ? document.getElementById('sub-botao_Regras de cálculo_mobile')!
                : document.getElementById('sub-botao_Regras de cálculo')!,
        parentElements: isMobilePresentation
            ? [
                  () => document.getElementById('botao_menuVertical')!,
                  () => document.getElementById('botao_Cadastros_mobile')!,
              ]
            : [() => document.getElementById('botao_Cadastros')!],
    },
    {
        title: 'Crie as tabelas de horario',
        description: `Cadastre tabelas de horário, informando nome, tolerâncias e 
            horários de entrada e saída. Clique em "CADASTRAR".`,
        getHighlightElement: () =>
            isMobilePresentation
                ? document.getElementById(
                      'sub-botao_Tabela de horários_mobile',
                  )!
                : document.getElementById('sub-botao_Tabela de horários')!,
        parentElements: isMobilePresentation
            ? [
                  () => document.getElementById('botao_menuVertical')!,
                  () => document.getElementById('botao_Cadastros_mobile')!,
              ]
            : [() => document.getElementById('botao_Cadastros')!],
    },
    {
        title: 'Cadastre os funcionários',
        description: `Cadastre funcionários clicando em "Adicionar" e preenchendo os dados. Clique em "ADICIONAR".`,
        getHighlightElement: () =>
            isMobilePresentation
                ? document.getElementById('sub-botao_Funcionários_mobile')!
                : document.getElementById('sub-botao_Funcionários')!,
        parentElements: isMobilePresentation
            ? [
                  () => document.getElementById('botao_menuVertical')!,
                  () => document.getElementById('botao_Cadastros_mobile')!,
              ]
            : [() => document.getElementById('botao_Cadastros')!],
    },
    {
        title: 'Registre o ponto manualmente',
        description: `Registre o ponto manualmente selecionando funcionário, empresa, data e horários.`,
        getHighlightElement: () =>
            isMobilePresentation
                ? document.getElementById('sub-botao_Digitação_mobile')!
                : document.getElementById('sub-botao_Digitação')!,
        parentElements: isMobilePresentation
            ? [
                  () => document.getElementById('botao_menuVertical')!,
                  () =>
                      document.getElementById(
                          'botao_Manutenção do ponto_mobile',
                      )!,
              ]
            : [() => document.getElementById('botao_Manutenção do ponto')!],
    },
    {
        title: 'Importar registros',
        description: `Importe registros de ponto (AFD) caso utilize um relógio de ponto.`,
        getHighlightElement: () =>
            isMobilePresentation
                ? document.getElementById('sub-botao_AFD_mobile')!
                : document.getElementById('sub-botao_AFD')!,
        parentElements: isMobilePresentation
            ? [
                  () => document.getElementById('botao_menuVertical')!,
                  () => document.getElementById('botao_Integrações_mobile')!,
              ]
            : [() => document.getElementById('botao_Integrações')!],
    },
    {
        title: 'Baixar aplicativo',
        description: `Baixe o aplicativo para Android e iOS para acessar o ponto de qualquer lugar.`,
    },
    {
        title: 'Relógio de ponto',
        description: `Ou use um relógio de ponto para registrar o ponto dos funcionários, 
            entre em contato com o suporte para configuração.`,
    },
    {
        title: 'Calcule o ponto',
        description: `Por fim, calcule o ponto dos funcionários em massa 
            usando a opção de cálculo automático da Ponto Ágil.`,
        getHighlightElement: () =>
            isMobilePresentation
                ? document.getElementById('sub-botao_Cálculo do ponto_mobile')!
                : document.getElementById('sub-botao_Cálculo do ponto')!,
        parentElements: isMobilePresentation
            ? [
                  () => document.getElementById('botao_menuVertical')!,
                  () =>
                      document.getElementById(
                          'botao_Manutenção do ponto_mobile',
                      )!,
              ]
            : [() => document.getElementById('botao_Manutenção do ponto')!],
    },
]

export function hasPermissionAjustes() {
    return (
        hasPermission(['update', 'Registros']) ||
        hasPermission(['update', 'Abonos']) ||
        hasPermissionToAnyAfastamento('update')
    )
}
